import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import StoreLayout from "@/pages/store/StoreLayout";
import Home from "@/pages/store/Home";
import Catalogue from "@/pages/store/Catalogue";
import Cart from "@/pages/store/Cart";
import OrderHistory from "@/pages/store/OrderHistory";
import StoreContent from "@/pages/store/StoreContent";
import AdminLayout from "@/pages/admin/AdminLayout";
import Hub from "@/pages/admin/Hub";
import AdminOrders from "@/pages/admin/Orders";
import Stores from "@/pages/admin/Stores";
import FlowerImages from "@/pages/admin/FlowerImages";
import HomeContent from "@/pages/admin/HomeContent";
import LandingPage from "@/pages/admin/LandingPage";
import Users from "@/pages/admin/Users";
import ActivityLog from "@/pages/admin/ActivityLog";
import Account from "@/pages/admin/Account";
import AppearanceLoader from "@/components/AppearanceLoader";
import MissingOrders from "@/pages/admin/MissingOrders";
import StandingOrders from "@/pages/admin/StandingOrders";
import AdminEvents from "@/pages/admin/Events";
import Specials from "@/pages/admin/Specials";
import Production from "@/pages/admin/Production";
import StoreOrdersReport from "@/pages/admin/StoreOrdersReport";
import Schedule from "@/pages/admin/Schedule";

function Splash() {
  return (
    <div className="bg-terracotta grid min-h-screen place-items-center">
      <div className="animate-pulse font-serif text-3xl font-extrabold text-white">Hasfarm…</div>
    </div>
  );
}

function StoreProtected({ children }) {
  const { store, loading } = useAuth();
  if (loading || store === null) return <Splash />;
  if (!store) return <Navigate to="/login" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <Splash />;
  if (!admin) return <Navigate to="/login?role=admin" replace />;
  return children;
}

function Landing() {
  const { store, admin, loading } = useAuth();
  if (loading || store === null) return <Splash />;
  if (store) return <Navigate to="/store" replace />;
  if (admin) return <Navigate to="/admin" replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppearanceLoader />
          <Toaster position="bottom-center" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/store" element={<StoreProtected><StoreLayout /></StoreProtected>}>
              <Route index element={<Home />} />
              <Route path="catalogue" element={<Catalogue />} />
              <Route path="cart" element={<Cart />} />
              <Route path="orders" element={<OrderHistory />} />
              <Route path="content/:key" element={<StoreContent />} />
            </Route>
            <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
              <Route index element={<Hub />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="missing-orders" element={<MissingOrders />} />
              <Route path="standing-orders" element={<StandingOrders />} />
              <Route path="specials" element={<Specials />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="stores" element={<Stores />} />
              <Route path="flowers" element={<FlowerImages />} />
              <Route path="home-content" element={<HomeContent />} />
              <Route path="landing-page" element={<LandingPage />} />
              <Route path="users" element={<Users />} />
              <Route path="activity" element={<ActivityLog />} />
              <Route path="account" element={<Account />} />
              <Route path="production" element={<Production />} />
              <Route path="store-orders" element={<StoreOrdersReport />} />
              <Route path="schedule" element={<Schedule />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
